import { useEffect, useMemo, useState } from "react";
import { EmptyState, Skeleton } from "@/design-system/components/ui";
import { formatDateInTz } from "@/design-system/lib/format";
import { brokeragePortalApi, type BrokerageRosterRow } from "../api/brokerage";
import type { BrokerDashboardChipGroup, PortalDealSubmissionListDto } from "../api/types";

// Brokerage / Sub-Broker PR-6 — the Deals tab: the roster's deal-submission
// status dashboard (the portal twin of the broker "Deals & status" surface),
// re-scoped across the firm. Filter by status chip + sub-broker (the drill-down)
// + free-text; DB-paginated.

const CHIPS: { id: BrokerDashboardChipGroup; label: string }[] = [
  { id: "All", label: "All" },
  { id: "Drafts", label: "Drafts" },
  { id: "Submitted", label: "Submitted" },
  { id: "InStaging", label: "In staging" },
  { id: "Returned", label: "Returned" },
  { id: "Active", label: "Active" },
  { id: "Closed", label: "Closed" },
  { id: "DeclinedOrWithdrawn", label: "Declined / withdrawn" },
];

const PAGE_SIZE = 20;

function chipClass(active: boolean): string {
  return active
    ? "rounded-full bg-onyx px-space-3 py-space-1 text-caption font-medium text-white-pure"
    : "rounded-full border border-border-light px-space-3 py-space-1 text-caption font-medium text-gray-mid hover:border-gold";
}

function attentionLabel(it: { outstandingCount: number; openReturnCommentCount: number }): string {
  const parts: string[] = [];
  if (it.openReturnCommentCount > 0) parts.push(`${it.openReturnCommentCount} to address`);
  if (it.outstandingCount > 0) parts.push(`${it.outstandingCount} incomplete`);
  return parts.length ? parts.join(" · ") : "—";
}

export function BrokerageDealsTab() {
  const [group, setGroup] = useState<BrokerDashboardChipGroup>("All");
  const [subBrokerId, setSubBrokerId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<PortalDealSubmissionListDto | null>(null);
  const [roster, setRoster] = useState<BrokerageRosterRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Roster once — populates the sub-broker drill-down filter. A failure here just
  // leaves the filter empty; the deals list still loads.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await brokeragePortalApi.roster();
        if (!cancelled) setRoster(r);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const d = await brokeragePortalApi.deals({
          group,
          q: q || undefined,
          page,
          pageSize: PAGE_SIZE,
          subBrokerId: subBrokerId || undefined,
        });
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      } catch (err) {
        if (!cancelled)
          setError(err instanceof Error ? err.message : "Couldn't load your firm's deals.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [group, q, page, subBrokerId]);

  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.totalCount / data.pageSize)) : 1),
    [data],
  );

  return (
    <div className="space-y-space-4">
      <div className="flex flex-wrap gap-space-2">
        {CHIPS.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => {
              setGroup(c.id);
              setPage(1);
            }}
            className={chipClass(group === c.id)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-space-3">
        <select
          value={subBrokerId}
          aria-label="Filter by sub-broker"
          onChange={(e) => {
            setSubBrokerId(e.target.value);
            setPage(1);
          }}
          className="rounded-lg border border-border-light bg-white-pure px-space-3 py-space-2 text-small text-onyx"
        >
          <option value="">All sub-brokers</option>
          {roster.map((r) => (
            <option key={r.subBrokerId} value={r.subBrokerId}>
              {r.subBrokerName}
            </option>
          ))}
        </select>
        <form
          className="flex flex-1 gap-space-2"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(searchInput.trim());
            setPage(1);
          }}
        >
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by business, title, or deal number"
            className="flex-1 rounded-lg border border-border-light bg-white-pure px-space-3 py-space-2 text-small text-onyx"
          />
          <button
            type="submit"
            className="rounded-lg border border-border-light px-space-3 py-space-2 text-small font-medium text-onyx hover:border-gold"
          >
            Search
          </button>
        </form>
      </div>

      {error && <p className="text-small text-status-error">{error}</p>}
      {loading && !data && <Skeleton className="h-40 w-full" />}
      {data && data.items.length === 0 && !loading && (
        <EmptyState title="No deals found" description="No deals match the current filters." />
      )}
      {data && data.items.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border-light">
          <table className="w-full text-small">
            <thead>
              <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
                <th className="px-space-3 py-space-2 font-medium">Business</th>
                <th className="px-space-3 py-space-2 font-medium">Deal #</th>
                <th className="px-space-3 py-space-2 font-medium">Status</th>
                <th className="px-space-3 py-space-2 font-medium">Last updated</th>
                <th className="px-space-3 py-space-2 font-medium">Needs attention</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} className="border-b border-border-light last:border-0">
                  <td className="px-space-3 py-space-2 text-onyx">
                    {it.businessName ?? it.title ?? "Untitled submission"}
                  </td>
                  <td className="px-space-3 py-space-2 text-gray-mid">{it.dealNumber ?? "—"}</td>
                  <td className="px-space-3 py-space-2 text-gray-mid">{it.stageLabel}</td>
                  <td className="px-space-3 py-space-2 text-gray-mid">
                    {formatDateInTz(it.lastUpdatedAt)}
                  </td>
                  <td className="px-space-3 py-space-2 text-gray-mid">{attentionLabel(it)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.totalCount > data.pageSize && (
        <div className="flex items-center justify-between">
          <span className="text-caption text-gray-mid">
            Page {data.page} of {totalPages} · {data.totalCount} total
          </span>
          <div className="flex gap-space-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded-lg border border-border-light px-space-3 py-space-1 text-small font-medium text-onyx enabled:hover:border-gold disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded-lg border border-border-light px-space-3 py-space-1 text-small font-medium text-onyx enabled:hover:border-gold disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
